import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { toast } from "sonner";
import { ArrowLeft, Calendar, User, DollarSign, Clock, FileText } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";

export default function EntryHistory() {
  const navigate = useNavigate();
  const { user, getAuthHeader } = useAuth();
  const [entries, setEntries] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchEntries = async () => {
      try {
        const endpoint = user?.role === "admin" ? "/job-entries/all" : "/job-entries";
        const response = await axios.get(`${API_URL}${endpoint}`, {
          headers: getAuthHeader(),
        });
        setEntries(response.data);
      } catch (error) {
        console.error("Failed to fetch entries:", error);
        toast.error("Failed to load entries");
      } finally {
        setIsLoading(false);
      }
    };
    fetchEntries();
  }, [user, getAuthHeader]);

  const formatDate = (isoString) => {
    const date = new Date(isoString);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse text-primary font-oswald text-xl">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="glass-header sticky top-0 z-40 px-4 py-3">
        <div className="max-w-2xl mx-auto flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/")}
            className="text-slate-600"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="font-oswald font-bold text-[#003366] text-xl tracking-tight">
              {user?.role === "admin" ? "ALL ENTRIES" : "MY ENTRIES"}
            </h1>
            <p className="text-xs text-slate-500">{entries.length} job entries</p>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-2xl mx-auto px-4 py-6 space-y-4 pb-24">
        {entries.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center">
              <FileText className="w-12 h-12 mx-auto text-slate-300 mb-4" />
              <h3 className="font-oswald text-xl text-slate-600 mb-2">No Entries Yet</h3>
              <p className="text-slate-500">Job entries will appear here after you save them.</p>
              <Button
                onClick={() => navigate("/")}
                className="mt-6 bg-[#003366] hover:bg-[#002244]"
                data-testid="go-to-dashboard-btn"
              >
                Go to Dashboard
              </Button>
            </CardContent>
          </Card>
        ) : (
          entries.map((entry, index) => (
            <Card
              key={entry.id}
              className="card-hover animate-fade-in"
              style={{ animationDelay: `${index * 50}ms` }}
              data-testid={`entry-card-${entry.id}`}
            >
              <CardContent className="p-5">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <div className="font-oswald text-2xl font-bold text-[#003366]">
                      ${entry.customer_price.toLocaleString()}
                    </div>
                    <div className="flex items-center gap-4 text-sm text-slate-500 mt-1">
                      <span className="flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        {entry.labor_hours}h
                      </span>
                      <span className="flex items-center gap-1">
                        <DollarSign className="w-4 h-4" />
                        ${entry.materials_cost} materials
                      </span>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="flex items-center gap-1 text-xs text-slate-500">
                      <Calendar className="w-3 h-3" />
                      {formatDate(entry.created_at)}
                    </div>
                    {user?.role === "admin" && (
                      <div className="flex items-center gap-1 text-xs text-slate-500 mt-1">
                        <User className="w-3 h-3" />
                        {entry.user_name}
                      </div>
                    )}
                  </div>
                </div>

                <div className="space-y-3">
                  <div>
                    <p className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-1">
                      Technician Notes
                    </p>
                    <p className="text-sm text-slate-600 bg-slate-50 rounded-lg p-3">
                      {entry.technician_notes}
                    </p>
                  </div>

                  <div>
                    <p className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-1">
                      Customer Summary
                    </p>
                    <p className="text-sm text-slate-700 bg-blue-50 rounded-lg p-3 border border-blue-100">
                      {entry.customer_summary}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </main>
    </div>
  );
}
